import React, { useState, useEffect } from 'react';
import { SuryaMark } from '@/components/brand/SuryaMark';
import { useAuth } from '@/lib/auth';
import { EnvironmentChip } from '@/components/layout/EnvironmentChip';
import { Lock, ArrowRight, AlertCircle } from 'lucide-react';

export function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);

  // Check if SSO is enabled
  useEffect(() => {
    fetch('/api/auth/sso/status')
      .then((r) => r.json())
      .then((data) => {
        if (data.sso_enabled) setSsoEnabled(true);
      })
      .catch(() => {}); // SSO status check is best-effort
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
    } catch (err: any) {
      setError(err.message || 'Login failed');
    }
    setLoading(false);
  };

  const handleSSOLogin = async () => {
    setSsoLoading(true);
    setError('');
    try {
      const redirectUri = `${window.location.origin}/sso/callback`;
      const res = await fetch(`/api/auth/sso/microsoft?redirect_uri=${encodeURIComponent(redirectUri)}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'SSO unavailable' }));
        throw new Error(err.detail || 'SSO unavailable');
      }
      const data = await res.json();
      window.location.href = data.auth_url;
    } catch (err: any) {
      setError(err.message || 'SSO login failed');
      setSsoLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          {/* SPEC-459 (scope addition) — one wordmark lockup everywhere. See the comment
              in components/layout/Sidebar.tsx for the reasoning; the sidebar moved to THIS
              treatment (DM Sans bold, tracking-tight), and the accent moved onto "IQ" so
              the name has a form of its own rather than being a coloured heading. */}
          {/* SPEC-462 — the Planning Dial mark at 64px above the lockup. Decorative: the
              wordmark beneath names the product. */}
          <SuryaMark size={64} className="mx-auto mb-4 text-[var(--text-primary)]" />
          <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">
            <span>Surya</span><span className="text-amber-400">IQ</span>
          </h1>
          <p className="text-xs text-[var(--text-tertiary)] mt-1">Where the business runs</p>
          <EnvironmentChip className="mt-3" />
        </div>

        {/* Login card */}
        <div className="bg-[var(--bg-raised)] border border-[var(--border-default)] rounded-xl p-6 shadow-xl">
          <div className="flex items-center gap-2 mb-5">
            <Lock className="w-4 h-4 text-[var(--text-tertiary)]" />
            <h2 className="text-sm font-medium text-[var(--text-primary)]">Sign in to continue</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs text-[var(--text-tertiary)] mb-1">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-3 py-2.5 text-sm bg-[var(--bg-overlay)] border border-[var(--border-default)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                placeholder="Enter username"
                autoFocus
                autoComplete="username"
              />
            </div>
            <div>
              <label className="block text-xs text-[var(--text-tertiary)] mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 text-sm bg-[var(--bg-overlay)] border border-[var(--border-default)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                placeholder="Enter password"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 p-2.5 bg-red-500/10 border border-red-500/20 rounded-lg">
                <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                <p className="text-xs text-red-400">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !username || !password}
              className="w-full py-2.5 bg-amber-500 text-[var(--text-inverse)] rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
            >
              {loading ? 'Signing in...' : (
                <>
                  Sign in
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Microsoft SSO — only visible when SSO_ENABLED=true on server */}
          {ssoEnabled && (
            <>
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-[var(--border-default)]" />
                <span className="text-[10px] text-[var(--text-tertiary)] uppercase">or</span>
                <div className="flex-1 h-px bg-[var(--border-default)]" />
              </div>
              <button
                onClick={handleSSOLogin}
                disabled={ssoLoading}
                className="w-full py-2.5 bg-[var(--bg-overlay)] border border-[var(--border-default)] rounded-lg hover:bg-[var(--bg-base)] disabled:opacity-50 transition-colors flex items-center justify-center gap-2 text-sm text-[var(--text-primary)]"
              >
                <svg className="w-4 h-4" viewBox="0 0 21 21" fill="none">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                {ssoLoading ? 'Redirecting...' : 'Sign in with Microsoft'}
              </button>
            </>
          )}
        </div>

        <p className="text-center text-[10px] text-[var(--text-tertiary)] mt-6">
          Flying Trade Group — Internal Platform
        </p>
      </div>
    </div>
  );
}
